import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Offcanvas, Modal, Dropdown } from "react-bootstrap";
import ConfirmModal from "../../components/modals/ConfirmModal";
import DeviceAttributesPanel from "./DeviceAttributesPanel";
import Select from "react-select";
import { MdOutlineBluetoothDisabled } from "react-icons/md";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import StarekContext from "../../context/StarekContext";
import { SaveSuccessPanel } from "../../components/Sidebar";
import {
    GET_OPERATIONS_FOR_PLANT,
    GET_DEVICE_CAPABILITIES,
    operationToOption,
    saveDeviceCapabilities,
} from "./deviceCapabilities";
import {
    FALLBACK_DEVICE_FLAGS,
    computeGpioType,
    decodeGpioType,
    computeUartType,
    decodeUartType,
    normalizeRuntimeSignals,
    compareSignalKeys,
    computePhysicalSignalColors,
    manifestDefaultType,
} from "./gpioTypeUtils";
import {
    fetchAvailableDeviceModels,
    FALLBACK_SIGNAL_COLORS,
} from "../deviceProfile/AddDeviceProfileWizard";
import {
    ANALYZER_KINDS,
    ANALYZER_KIND_FIELDS,
    defaultAnalyzerConfig,
} from "./analyzerSchema";

const AVAILABLE_LOCATIONS = [
    "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai",
    "Kolkata", "Pune", "Ahmedabad", "Jaipur", "Surat",
];

// Every GPIO port is fundamentally this same 3-axis bitmask
// (Discrete/Proportional, Sense/Actuate, Control/State) regardless of
// whether a given model's manifest happens to declare selectable dropdowns
// for it in the profile-authoring wizard - a "fixed-type" model like
// starek-nwm-sim just ships with one axis combination as its default, it
// doesn't mean the hardware can't be overridden per-device. So this is
// always decoded/composed from the raw bits, never gated on manifest
// declaration.
const GPIO_TYPE_AXES = [
    { name: "discreteOrProportional", options: ["Discrete", "Proportional"] },
    { name: "actuateOrSense", options: ["Sense", "Actuate"] },
    { name: "controlOrState", options: ["Control", "State"] },
];

// UART's peripheral-init parameters - same category as GPIO's axes above
// ("part of initializing a peripheral"), just rendered as plain selects
// instead of toggle badges since each one has more than 2 options. Label
// text mirrors the manifest's own parameter names.
const UART_PARAMS = [
    { name: "baudrate", label: "Baud" },
    { name: "data_bits", label: "Data bits" },
    { name: "parity", label: "Parity" },
    { name: "stop_bits", label: "Stop bits" },
];

// Icon + tooltip per axis option and per boolean toggle - badges show an
// icon instead of text to stay compact; the title attribute carries the
// full explanation on hover since the icon alone can't.
const AXIS_ICON_INFO = {
    Discrete: {
        icon: "fa-wave-square",
        help: "Discrete: reads/writes a simple on/off (digital) value. Click to switch to Proportional.",
    },
    Proportional: {
        icon: "fa-chart-line",
        help: "Proportional: reads/writes a continuously variable (analog) value. Click to switch to Discrete.",
    },
    Sense: {
        icon: "fa-eye",
        help: "Sense: this port reads an external signal (input). Click to switch to Actuate.",
    },
    Actuate: {
        icon: "fa-bolt",
        help: "Actuate: this port drives an external signal (output). Click to switch to Sense.",
    },
    Control: {
        icon: "fa-sliders-h",
        help: "Control. Click to switch to State.",
    },
    State: {
        icon: "fa-info-circle",
        help: "State. Click to switch to Control.",
    },
};
const selectMenuPortalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

// A native <option>'s inline `style.color` is ignored by most browsers'
// OS-rendered dropdown popups (confirmed - Chrome/macOS shows plain black
// text regardless), so the Source dropdown's color dot needs a real custom
// dropdown instead of a styled native <select> - react-select (already used
// above for the Device Profile picker) renders its own menu in the page,
// where formatOptionLabel's colored <span> actually shows.
const formatSourceOption = (opt) => (
    <div className="tw-flex tw-items-center tw-gap-2">
        <span
            className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-shrink-0"
            style={{ backgroundColor: `#${opt.color || "000000"}` }}
        />
        <span>
            {opt.label}
            {opt.disabled ? " (already in use)" : ""}
        </span>
    </div>
);

/**
 * What a machine can do only pays off elsewhere: a part's route step offers the machines
 * that list its operation, and a run picks one of those. Neither screen is reachable from
 * here, so the save points at them.
 */
const capabilityNextSteps = (deviceId, linkedCount) => ({
    heading: `Capability updated for ${deviceId}`,
    subheading:
        linkedCount === 0
            ? "It lists no operations now, so no route step can pick it."
            : `It can now be picked for ${linkedCount} operation${linkedCount === 1 ? "" : "s"}.`,
    steps: [
        {
            label: "Use this machine in a part's route",
            why: "A route step only offers machines that list its operation.",
            path: ["Setup", "Parts", "open a part", "Route"],
            to: "/parts",
        },
        {
            label: "Schedule a run on it",
            why: "The run assigns one machine per step and tracks it from there.",
            path: ["Plan", "Job Schedules", "Add"],
            to: "/job-schedules",
        },
    ],
});

// Tab order in the edit sidebar. "operations" is the device's Capability
// list (which operations it can perform); the tab is labelled Operations
// because that is what the panel itself edits and what users look for.
const SIDEBAR_TABS = [
    { key: "signals", label: "Signals" },
    { key: "interpretation", label: "Interpretation" },
    { key: "attributes", label: "Attributes" },
    { key: "locations", label: "Locations" },
    { key: "capabilities", label: "Capability" },
];

// Standard entity-style edit sidebar: signal configuration, attribute
// editing (DeviceAttributesPanel), device profile assignment, and location
// linking. Live monitoring (the signal graph, channel switcher) lives in
// the LiveView modal instead (see Devices.jsx's liveViewDevice state /
// onShowLiveView) - the grid no longer has an inline expand row for this.
const DeviceEditSidebar = ({
    isOpen,
    onClose,
    device,
    deviceProfiles = [],
    canWrite = false,
    onSaveSignals,
    onAssignProfile,
    onUnassignProfile,
    onUpdateSignalType,
    onSaveAsProfile,
    onSaveLogicalSignal,
    onSaveInterpretations,
    onShowLiveView,
    initialTab = "signals",
}) => {
    const starekContext = useContext(StarekContext);
    const deviceFlags = starekContext?.types?.device?.flags || FALLBACK_DEVICE_FLAGS;
    const defaultSignalColors =
        starekContext?.types?.device?.defaultSignalColors || FALLBACK_SIGNAL_COLORS;

    const [activeTab, setActiveTab] = useState("signals");
    // Lets a caller (e.g. LiveView's "+ Create interpretation" banner) open
    // the sidebar directly on a specific tab - only applied on the opening
    // transition, so it doesn't fight the user's own tab clicks once open.
    useEffect(() => {
        if (isOpen) setActiveTab(initialTab || "signals");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialTab]);

    // --- Operations this device can perform (Capability mapping) ---
    const navigate = useNavigate();
    const [saveOutcome, setSaveOutcome] = useState(null);
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [savingOperations, setSavingOperations] = useState(false);

    const { data: operationsData, loading: loadingOperations } = useQuery(
        GET_OPERATIONS_FOR_PLANT,
        {
            client: starekContext.engineClient,
            skip: !isOpen || !starekContext.lastUsedPlant?.id,
            variables: {
                where: {
                    plant_id: { equals: starekContext.lastUsedPlant?.id },
                    is_active: { equals: true },
                },
            },
        },
    );

    const {
        data: capabilitiesData,
        loading: loadingCapabilities,
        refetch: refetchCapabilities,
    } = useQuery(GET_DEVICE_CAPABILITIES, {
        client: starekContext.engineClient,
        skip: !isOpen || !device?.id,
        variables: { deviceId: device?.id },
        fetchPolicy: "network-only",
    });

    const operationOptions = useMemo(
        () => (operationsData?.operations || []).map(operationToOption),
        [operationsData?.operations],
    );

    const linkedOperationIds = useMemo(
        () =>
            (capabilitiesData?.capabilities || []).map((c) =>
                Number(c.operation_id),
            ),
        [capabilitiesData?.capabilities],
    );

    useEffect(() => {
        setSaveOutcome(null);
    }, [isOpen, device?.id]);

    // Reset the picker to what is actually stored whenever the device or its links change.
    useEffect(() => {
        const linked = (capabilitiesData?.capabilities || []).map((c) => ({
            value: Number(c.operation_id),
            label: c.operation?.name || `Operation ${c.operation_id}`,
        }));
        setSelectedOperations(linked);
    }, [capabilitiesData?.capabilities, device?.id]);

    const handleSaveOperations = async () => {
        if (!device?.id) return;
        setSavingOperations(true);
        try {
            const { added, removed } = await saveDeviceCapabilities(
                starekContext.engineClient,
                device.id,
                selectedOperations.map((o) => o.value),
                linkedOperationIds,
            );
            await refetchCapabilities();
            if (added || removed) {
                starekContext.showSuccess(
                    `Operations updated for ${device.id} (${added} added, ${removed} removed)`,
                );
                // Something changed, so there is a next step worth naming. A no-op
                // save has nothing to say and closes as it always did.
                setSaveOutcome(
                    capabilityNextSteps(device.id, selectedOperations.length),
                );
                return;
            }
            onClose?.();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setSavingOperations(false);
        }
    };

    const handleUnlinkOperation = (operationId) => {
        setSelectedOperations((prev) =>
            prev.filter((o) => o.value !== operationId),
        );
    };

    const handleStepNavigate = (to) => {
        onClose?.();
        navigate(to);
    };

    const [signals, setSignals] = useState([]);
    const [typeSavingKey, setTypeSavingKey] = useState(null);
    // Interpretations - business-context signals layered on read-only
    // physical signals (see the plan's physical vs interpretation split).
    // Each entry: {name, source, analyzer, i2c_address?, i2c_register?,
    // inverted, auxillary, color}, matching updateDeviceInterpretations'
    // wire shape exactly.
    const [interpretations, setInterpretations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [linkedLocations, setLinkedLocations] = useState([]);
    const [selectedProfileId, setSelectedProfileId] = useState("");
    const [rebootNow, setRebootNow] = useState(false);
    const [profileActionBusy, setProfileActionBusy] = useState(false);
    const [showIncompatibleProfiles, setShowIncompatibleProfiles] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showSaveAsProfileModal, setShowSaveAsProfileModal] = useState(false);
    // "+ New Interpretation" form state.
    const [newSignalName, setNewSignalName] = useState("");
    const [newSignalEndpoint, setNewSignalEndpoint] = useState("");
    const [newSignalI2cAddress, setNewSignalI2cAddress] = useState("");
    const [newSignalI2cRegister, setNewSignalI2cRegister] = useState("");
    const [newSignalAnalyzer, setNewSignalAnalyzer] = useState(
        defaultAnalyzerConfig(ANALYZER_KINDS[0].value),
    );
    const [newInterpInverted, setNewInterpInverted] = useState(false);
    const [newInterpAuxillary, setNewInterpAuxillary] = useState(false);
    const [newInterpColor, setNewInterpColor] = useState(defaultSignalColors[0]);
    const [savingLogicalSignal, setSavingLogicalSignal] = useState(false);
    // Which interpretation card is expanded into its editable form - cards
    // collapse to a compact view by default (name/color/caption + an Edit
    // button). editingKey holds the interpretation's `name` being edited
    // (its identifier - interpretations, unlike physical signals, can be
    // renamed), seeded on Edit click - separate from newSignal*'s state,
    // which stays scoped to the "add a new interpretation" card.
    const [editingKey, setEditingKey] = useState(null);
    const [editName, setEditName] = useState("");
    const [editEndpoint, setEditEndpoint] = useState("");
    const [editI2cAddress, setEditI2cAddress] = useState("");
    const [editI2cRegister, setEditI2cRegister] = useState("");
    const [editAnalyzer, setEditAnalyzer] = useState(
        defaultAnalyzerConfig(ANALYZER_KINDS[0].value),
    );
    const [editInterpInverted, setEditInterpInverted] = useState(false);
    const [editInterpAuxillary, setEditInterpAuxillary] = useState(false);
    const [editInterpColor, setEditInterpColor] = useState(defaultSignalColors[0]);
    const [savingEdit, setSavingEdit] = useState(false);
    // Cross-tab navigation from a physical signal's row (Signals tab) to a
    // specific interpretation or to a pre-filled "+ New Interpretation"
    // form (Interpretation tab) - both tabs render conditionally, so the
    // actual scroll/focus has to wait until the target tab is mounted (see
    // the effects below), not fire immediately on click.
    const [highlightInterpName, setHighlightInterpName] = useState(null);
    const [pendingFocusNewInterpretation, setPendingFocusNewInterpretation] = useState(false);
    const newInterpretationSectionRef = useRef(null);
    const newInterpretationNameInputRef = useRef(null);
    const interpretationCardRefs = useRef({});
    // Same ConfirmModal/openConfirm/closeConfirm pattern Devices.jsx uses
    // for its own destructive actions (Unbind/Reboot/Factory reset) -
    // local to this component rather than lifted to the parent, since
    // delete/reset here are purely this tab's own concern.
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });
    const [saveAsProfileName, setSaveAsProfileName] = useState("");
    const [saveAsProfileDescription, setSaveAsProfileDescription] = useState("");
    const [savingAsProfile, setSavingAsProfile] = useState(false);
    // Bumped after our own assign/unassign completes to force a resync from
    // the (by then fresh) device prop - see the effect below. Not driven by
    // device?.parameters directly, since GET_DEVICES_IN_PLANT polls every
    // 60s and that would clobber an in-progress unsaved edit.
    const [refreshTick, setRefreshTick] = useState(0);

    // What "active" signals config governs this device right now, in the
    // same precedence order as everywhere else in this feature: per-device
    // overrides win, then the assigned Device Profile's own signals, then
    // the device's stock model manifest. This tier is ONLY the profile/model
    // fallback source (see the signals-seeding effect below for where
    // device.parameters - tier 1 - is checked first) - a device with a
    // profile assigned but no per-device overrides yet has nothing in
    // device.parameters, and without this fallback the Signals tab showed
    // "No signals configured yet" despite the device actively publishing
    // data per the profile's config. The profile lookup is synchronous
    // (deviceProfiles is already a loaded prop); only the stock-model
    // fallback needs a fetch.
    const [activeManifestSignals, setActiveManifestSignals] = useState(null);
    useEffect(() => {
        if (!isOpen || !device) return;
        let cancelled = false;
        const profileId = device?.device_profile?.id;
        if (profileId) {
            const profile = deviceProfiles.find(
                (p) => String(p.id) === String(profileId),
            );
            setActiveManifestSignals(profile?.configuration?.signals || null);
            return;
        }
        if (!device?.device?.model) {
            setActiveManifestSignals(null);
            return;
        }
        fetchAvailableDeviceModels(starekContext)
            .then((models) => {
                if (cancelled) return;
                const model = models.find((m) => m.code === device.device.model);
                setActiveManifestSignals(model?.downlink?.signals || null);
            })
            .catch(() => {
                if (!cancelled) setActiveManifestSignals(null);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, device?.device_profile?.id, device?.device?.model, deviceProfiles]);

    // Only reset the editable signal form when the sidebar opens, switches
    // to a different device, refreshTick is bumped after our own
    // assign/unassign/save completes, or activeManifestSignals finishes
    // loading (the tier-3 stock-model fetch above is async, so this effect
    // may need to re-run once it resolves) - not on every background poll,
    // or an in-progress edit would get silently clobbered out from under
    // the user.
    // Physical signals only - a hardware fact (name/bus/axis-mode), never
    // renamed, never carrying cosmetics/analyzer here. Sourced from the
    // manifest (which addresses exist) cross-referenced with the currently
    // applicable type per address (override, else device-reported, else
    // the manifest's own axis defaults - see manifestDefaultType).
    useEffect(() => {
        if (!isOpen || !device) return;
        const runtimeSignals = normalizeRuntimeSignals(
            device?.device?.runtime?.signals,
        ).filter((s) => s?.analyzer === undefined); // interpretation-derived entries live in runtime.interpretations, not here
        const currentSignalTypes = new Map(
            runtimeSignals.map((s) => [s.name, s.type]),
        );
        const manifestEntryByAddress = {};
        if (activeManifestSignals) {
            Object.values(activeManifestSignals).forEach((entries) => {
                (Array.isArray(entries) ? entries : [entries]).forEach(
                    (entry) => {
                        const address = entry?.address || entry?.name;
                        if (address) manifestEntryByAddress[address] = entry;
                    },
                );
            });
        }
        // Manifest keys are guarded at insert above; runtime names are guarded
        // in normalizeRuntimeSignals. Filtered again here because everything
        // below treats an address as a string.
        const addresses = new Set(
            [
                ...Object.keys(manifestEntryByAddress),
                ...runtimeSignals.map((s) => s.name),
            ].filter((key) => typeof key === "string" && key),
        );
        setSignals(
            Array.from(addresses)
                .sort(compareSignalKeys)
                .map((key) => ({
                    key,
                    isGpio: key.startsWith("/gpio/"),
                    isUart: key.startsWith("/uart"),
                    manifestParams: manifestEntryByAddress[key]?.parameters,
                    type:
                        currentSignalTypes.get(key) ??
                        manifestDefaultType(
                            manifestEntryByAddress[key],
                            deviceFlags,
                        ),
                })),
        );
        setSelectedProfileId(device?.device_profile?.id ?? "");
        setRebootNow(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, refreshTick, activeManifestSignals]);

    // Interpretations (business-context signals - see the plan's physical
    // vs interpretation split) are purely backend-authored, no manifest
    // merging needed - just read straight off runtime.interpretations.
    useEffect(() => {
        if (!isOpen || !device) return;
        setInterpretations(device?.device?.runtime?.interpretations || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, refreshTick]);

    // ShadowDevice.parameters accumulates a cosmetics entry for every signal
    // address a device has EVER had saved against it - nothing prunes a key
    // when the device's active manifest changes (a rename, a removed
    // channel, an old firmware's virt signal that's since been dropped, or
    // switching to a Device Profile that remaps addresses entirely).
    // Cross-referencing against activeManifestSignals - the SAME tier-2/
    // tier-3 source used to seed signals above, not unconditionally the
    // device's own stock model - is what keeps orphaned entries like a
    // stale /virt/gps out, without also filtering out every profile-sourced
    // row whenever the assigned profile renames addresses (e.g. stock
    // /gpio/port0 remapped to a profile's /portA). null (not an empty Set)
    // means "not loaded yet", so the list renders unfiltered rather than
    // flashing empty.
    // Every physical signal this device actually has (all four buses -
    // GPIO ports included, not just uart/i2c) is a valid source a new
    // signal's analyzer can read from - derived from the same manifest
    // source the rest of this tab already uses, not a hardcoded bus list.
    const physicalEndpointOptions = useMemo(() => {
        if (!activeManifestSignals) return [];
        const options = [];
        Object.values(activeManifestSignals).forEach((entries) => {
            (Array.isArray(entries) ? entries : [entries]).forEach((entry) => {
                const address = entry?.address || entry?.name;
                if (address) options.push(address);
            });
        });
        // Object.values() here just reflects whatever order the manifest's
        // own bus keys happen to be in - not the deterministic bus/port
        // order the Signals tab (and LiveView) use, so without this sort
        // the Source dropdown's option order drifted out of sync with
        // every other listing of the same signals.
        return options.sort(compareSignalKeys);
    }, [activeManifestSignals]);

    // The same computed BBROYGBVGW color a physical signal shows in the
    // LiveView graph (see gpioTypeUtils.computePhysicalSignalColors) - used
    // here for the Source dropdown's color dot and as the default an
    // interpretation inherits from its source when it has no explicit
    // color of its own.
    const physicalSignalColors = useMemo(
        () => computePhysicalSignalColors(physicalEndpointOptions, defaultSignalColors),
        [physicalEndpointOptions, defaultSignalColors],
    );

    // Suggests the selected source's own computed color for a brand new
    // interpretation - an interpretation inherits its source signal's color
    // by default, unless the user overrides it here. Recomputed whenever
    // the chosen source changes (including the implicit default of
    // physicalEndpointOptions[0] before the user has touched the dropdown).
    useEffect(() => {
        const source = newSignalEndpoint || physicalEndpointOptions[0];
        setNewInterpColor(
            physicalSignalColors[source] || defaultSignalColors[0],
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSignalEndpoint, physicalEndpointOptions, physicalSignalColors]);

    // The list actually rendered - `signals` is already physical-only (see
    // the seeding effect above), so no further filtering is needed here.
    const visibleSignals = signals.map((signal, index) => ({ signal, index }));

    // A plain /uart source is a point-to-point wire with no addressing -
    // unlike I2C (addressed per-device via i2c_address/i2c_register) two
    // interpretations there can genuinely corrupt each other on the wire.
    // Mirrors the backend's own cap in updateDeviceInterpretations.
    const uartSourceTaken = useMemo(
        () => interpretations.some((i) => i.source === "/uart"),
        [interpretations],
    );

    // Which interpretations read from each physical signal - shown inline
    // on the Signals tab so a physical row relays its own interpretations
    // instead of requiring a trip to the Interpretation tab just to check.
    const interpretationsBySource = useMemo(() => {
        const map = {};
        interpretations.forEach((interp) => {
            if (!map[interp.source]) map[interp.source] = [];
            map[interp.source].push(interp);
        });
        return map;
    }, [interpretations]);

    // The Interpretation tab's content only mounts once activeTab actually
    // switches, so the scroll/focus targets don't exist yet at the moment
    // the triggering click happens on the Signals tab - these fire once
    // the tab (and its refs) are actually in the DOM.
    useEffect(() => {
        if (activeTab !== "interpretation" || !pendingFocusNewInterpretation) return;
        newInterpretationSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
        newInterpretationNameInputRef.current?.focus();
        setPendingFocusNewInterpretation(false);
    }, [activeTab, pendingFocusNewInterpretation]);

    useEffect(() => {
        if (activeTab !== "interpretation" || !highlightInterpName) return;
        interpretationCardRefs.current[highlightInterpName]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
        const timeout = setTimeout(() => setHighlightInterpName(null), 2000);
        return () => clearTimeout(timeout);
    }, [activeTab, highlightInterpName]);

    useEffect(() => {
        if (device?.location?.locationOption) {
            setLinkedLocations(device.location.locationOption);
        }

        const locationObj = device?.device?.runtime?.attributes?.location;
        if (locationObj?.latitude && locationObj?.longitude) {
            const { latitude, longitude } = locationObj;
            fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            )
                .then((res) => res.json())
                .then((resData) => {
                    const place = resData?.display_name || "Unknown location";
                    setLinkedLocations([place]);
                })
                .catch((err) => console.error("Location fetch failed", err));
        }
    }, [device]);

    const handleAddLocation = () => {
        if (selectedLocation && !linkedLocations.includes(selectedLocation)) {
            setLinkedLocations([...linkedLocations, selectedLocation]);
            setSelectedLocation("");
        }
    };
    const handleRemoveLocation = (locationToRemove) => {
        setLinkedLocations(linkedLocations.filter((l) => l !== locationToRemove));
    };

    const updateSignal = (index, field, value) => {
        setSignals((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleTypeChange = async (signal, newType) => {
        if (!device?.id || !onUpdateSignalType || signal.type === newType) return;
        setTypeSavingKey(signal.key);
        const previousType = signal.type;
        updateSignal(
            signals.findIndex((c) => c.key === signal.key),
            "type",
            newType,
        );
        try {
            await onUpdateSignalType(device.id, signal.key, newType);
            // No refreshTick bump here - the optimistic update above already
            // reflects the new type locally, and re-running the mount effect
            // would reset the whole form from device.parameters, silently
            // discarding any other unsaved edits (name/color/toggles) still
            // pending a Save Signals click.
        } catch (e) {
            // revert the optimistic update on failure
            updateSignal(
                signals.findIndex((c) => c.key === signal.key),
                "type",
                previousType,
            );
            starekContext.showError?.(e?.message || "Failed to update signal type");
        } finally {
            setTypeSavingKey(null);
        }
    };

    // Changing one of the three axes (Discrete/Proportional, Sense/Actuate,
    // Control/State) re-decodes the port's current type to get the other
    // two axes' values, overrides the changed one, and recomposes the full
    // bitmask - same assemble/decode pair the Device Profile wizard uses.
    const handleAxisChange = (signal, axisField, axisValue) => {
        const decoded = decodeGpioType(signal.type, deviceFlags, GPIO_TYPE_AXES);
        const newType = computeGpioType(
            { ...decoded, [axisField]: axisValue },
            deviceFlags,
        );
        handleTypeChange(signal, newType);
    };

    // Same decode/override/recompose pattern as handleAxisChange, for
    // UART's 4 peripheral-init parameters instead of GPIO's 3 axes.
    const handleUartParamChange = (signal, paramField, paramValue) => {
        const decoded = decodeUartType(signal.type, signal.manifestParams, deviceFlags);
        const newType = computeUartType(
            { ...decoded, [paramField]: paramValue },
            signal.manifestParams,
            deviceFlags,
        );
        handleTypeChange(signal, newType);
    };

    // Clears one physical signal's own axis-mode override (an
    // Device.overrides.signals entry with no analyzer field, keyed by
    // name), falling back to whatever the assigned profile or stock model
    // reports for it - same tier-2/tier-3 path used when a signal has never
    // had an override saved. Every other physical signal's override AND
    // every interpretation are re-sent unchanged, so only this one signal
    // is affected.
    const handleResetSignalToDefault = (signal) => {
        if (!device?.id || !onSaveLogicalSignal) return;
        openConfirm({
            title: `Reset ${signal.key}?`,
            message: `${signal.key}'s axis/mode override will be cleared, falling back to the assigned profile or stock model.`,
            type: "danger",
            confirmText: "Reset",
            onConfirm: async () => {
                setTypeSavingKey(signal.key);
                try {
                    // overriddenSignals (not the merged runtime.signals) -
                    // only this contains genuine overrides, so every OTHER
                    // signal here truly had one and isn't just inheriting
                    // its currently-reported value.
                    const existingOverrides = normalizeRuntimeSignals(
                        device?.device?.overriddenSignals,
                    );
                    const withoutThisSignal = existingOverrides.filter(
                        (s) => s?.analyzer !== undefined || s.name !== signal.key,
                    );
                    await onSaveLogicalSignal(
                        device.id,
                        withoutThisSignal,
                        rebootNow,
                    );
                    setRefreshTick((t) => t + 1);
                } finally {
                    setTypeSavingKey(null);
                }
                closeConfirm();
            },
        });
    };

    // --- Interpretation tab handlers ---

    // Cross-tab navigation, invoked from a physical signal's row.
    const handleJumpToInterpretation = (name) => {
        setHighlightInterpName(name);
        setActiveTab("interpretation");
    };
    const handleCreateInterpretationFor = (sourceKey) => {
        setNewSignalEndpoint(sourceKey);
        setPendingFocusNewInterpretation(true);
        setActiveTab("interpretation");
    };

    const resetNewInterpretationForm = () => {
        setNewSignalName("");
        setNewSignalEndpoint("");
        setNewSignalI2cAddress("");
        setNewSignalI2cRegister("");
        setNewSignalAnalyzer(defaultAnalyzerConfig(ANALYZER_KINDS[0].value));
        setNewInterpInverted(false);
        setNewInterpAuxillary(false);
        // Not resetting color here - clearing newSignalEndpoint above
        // already re-triggers the useEffect keyed on it, which recomputes
        // newInterpColor from physicalEndpointOptions[0]'s own color.
    };

    const handleAddInterpretation = async () => {
        const source = newSignalEndpoint || physicalEndpointOptions[0];
        const trimmedName = newSignalName.trim();
        if (
            !device?.id ||
            !onSaveInterpretations ||
            !trimmedName ||
            !source ||
            physicalEndpointOptions.includes(trimmedName) ||
            interpretations.some((i) => i.name === trimmedName) ||
            (source === "/uart" && uartSourceTaken)
        )
            return;
        setSavingLogicalSignal(true);
        try {
            const newEntry = {
                name: trimmedName,
                source,
                analyzer: newSignalAnalyzer,
                ...(source.startsWith("/i2c")
                    ? {
                        i2c_address: Number(newSignalI2cAddress) || 0,
                        i2c_register: Number(newSignalI2cRegister) || 0,
                    }
                    : {}),
                inverted: newInterpInverted,
                auxillary: newInterpAuxillary,
                // Only persist a color when it actually differs from the
                // source's own computed color - leaving it unset is what
                // makes "inherit from source by default" a live behavior
                // (DeviceGraph.jsx falls back to the source's color) rather
                // than a frozen snapshot of it at creation time.
                ...(newInterpColor !== physicalSignalColors[source]
                    ? { color: newInterpColor }
                    : {}),
            };
            const merged = [...interpretations, newEntry];
            await onSaveInterpretations(device.id, merged, rebootNow);
            setRefreshTick((t) => t + 1);
            resetNewInterpretationForm();
        } finally {
            setSavingLogicalSignal(false);
        }
    };

    const handleStartEditInterpretation = (interp) => {
        setEditName(interp.name);
        setEditEndpoint(interp.source || "");
        setEditAnalyzer(interp.analyzer || defaultAnalyzerConfig(ANALYZER_KINDS[0].value));
        setEditI2cAddress(interp.i2c_address ?? "");
        setEditI2cRegister(interp.i2c_register ?? "");
        setEditInterpInverted(!!interp.inverted);
        setEditInterpAuxillary(!!interp.auxillary);
        // Show the CURRENT effective color - an explicit override if one's
        // set, else whatever its source signal's computed color is right
        // now (not a stale value from whenever it was created).
        setEditInterpColor(
            interp.color ||
            physicalSignalColors[interp.source] ||
            defaultSignalColors[0],
        );
        setEditingKey(interp.name);
    };

    const handleCancelEdit = () => setEditingKey(null);

    const handleSaveEditInterpretation = async (originalName) => {
        if (!device?.id || !onSaveInterpretations) return;
        const trimmedName = editName.trim();
        const source = editEndpoint || physicalEndpointOptions[0];
        if (
            !trimmedName ||
            !source ||
            physicalEndpointOptions.includes(trimmedName) ||
            interpretations.some(
                (i) => i.name === trimmedName && i.name !== originalName,
            ) ||
            (source === "/uart" &&
                uartSourceTaken &&
                interpretations.find((i) => i.name === originalName)?.source !==
                "/uart")
        )
            return;
        setSavingEdit(true);
        try {
            const updatedEntry = {
                name: trimmedName,
                source,
                analyzer: editAnalyzer,
                ...(source.startsWith("/i2c")
                    ? {
                        i2c_address: Number(editI2cAddress) || 0,
                        i2c_register: Number(editI2cRegister) || 0,
                    }
                    : {}),
                inverted: editInterpInverted,
                auxillary: editInterpAuxillary,
                // Same omit-if-matches-source rule as handleAddInterpretation -
                // clearing back to the source's own color here (by leaving
                // this field unset) is what lets a later source-color change
                // actually reach a previously-overridden interpretation.
                ...(editInterpColor !== physicalSignalColors[source]
                    ? { color: editInterpColor }
                    : {}),
            };
            const merged = [
                ...interpretations.filter((i) => i.name !== originalName),
                updatedEntry,
            ];
            await onSaveInterpretations(device.id, merged, rebootNow);
            setRefreshTick((t) => t + 1);
            setEditingKey(null);
        } finally {
            setSavingEdit(false);
        }
    };

    // Removing an interpretation from the submitted list is ALSO how
    // "reset to profile default" works: the backend diffs whatever's
    // submitted against parameters.profileInterpretations, so a name that
    // still exists there simply reappears (its profile version), rather
    // than actually disappearing - only the button label/confirm copy
    // needs to reflect which of those two is about to happen.
    const handleDeleteInterpretation = (interp) => {
        if (!device?.id || !onSaveInterpretations) return;
        const profileInterpretations =
            device?.device?.runtime?.profileInterpretations || [];
        const isResettable = profileInterpretations.some(
            (p) => p.name === interp.name,
        );
        openConfirm({
            title: isResettable
                ? "Reset to profile default?"
                : "Delete interpretation?",
            message: isResettable
                ? `"${interp.name}" will revert to the value defined by this device's assigned profile.`
                : `Delete "${interp.name}"? This can't be undone from here.`,
            type: "danger",
            confirmText: isResettable ? "Reset" : "Delete",
            onConfirm: async () => {
                setSavingEdit(true);
                try {
                    const merged = interpretations.filter(
                        (i) => i.name !== interp.name,
                    );
                    await onSaveInterpretations(device.id, merged, rebootNow);
                    setRefreshTick((t) => t + 1);
                    setEditingKey(null);
                } finally {
                    setSavingEdit(false);
                }
                closeConfirm();
            },
        });
    };

    const handleAssignProfile = async () => {
        if (!device?.id || !selectedProfileId || !onAssignProfile) return;
        setProfileActionBusy(true);
        try {
            await onAssignProfile(device.id, Number(selectedProfileId), rebootNow);
            setRefreshTick((t) => t + 1);
        } finally {
            setProfileActionBusy(false);
        }
    };

    const handleUnassignProfile = async () => {
        if (!device?.id || !onUnassignProfile) return;
        setProfileActionBusy(true);
        try {
            await onUnassignProfile(device.id, rebootNow);
            setRefreshTick((t) => t + 1);
        } finally {
            setProfileActionBusy(false);
        }
    };

    // Snapshots this device's current effective config (whatever it
    // actually has right now, regardless of profile-vs-manual origin) into
    // a brand new Device Profile - the "device configured once, saved as a
    // reusable template" flow. Doesn't touch this device's own
    // device_profile_id/overrides at all.
    const handleSaveAsProfile = async () => {
        if (!device?.id || !saveAsProfileName || !onSaveAsProfile) return;
        setSavingAsProfile(true);
        try {
            await onSaveAsProfile(device.id, saveAsProfileName, saveAsProfileDescription);
            setShowSaveAsProfileModal(false);
            setSaveAsProfileName("");
            setSaveAsProfileDescription("");
        } finally {
            setSavingAsProfile(false);
        }
    };

    if (!isOpen || !device) return null;

    const assignedProfile = device.device_profile;
    const deviceModel = device.device?.model;
    // A profile's signal names/pins only make sense for the exact model it
    // was authored against (see backend's assignDeviceProfile, which
    // enforces this too) - a profile with no model set yet (still
    // unconfigured) isn't "incompatible", it just has nothing to conflict
    // with, so it's always shown.
    const isProfileCompatible = (p) => {
        const profileModel = p.configuration?.model?.code;
        return !profileModel || !deviceModel || profileModel === deviceModel;
    };
    const visibleProfiles = showIncompatibleProfiles
        ? deviceProfiles
        : deviceProfiles.filter(isProfileCompatible);
    const profileOptions = visibleProfiles.map((p) => ({
        value: p.id,
        label: p.name,
        isDisabled: !isProfileCompatible(p),
    }));
    const selectedProfileOption = selectedProfileId
        ? {
            value: selectedProfileId,
            label:
                deviceProfiles.find(
                    (p) => String(p.id) === String(selectedProfileId),
                )?.name || "",
        }
        : null;

    return (
        <>
            <Offcanvas
                show={isOpen}
                onHide={onClose}
                placement="end"
                backdrop={true}
                style={{
                    "--bs-offcanvas-width": "unset",
                    width: "600",
                    "--bs-offcanvas-height": "unset",
                    height: "100%",
                }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <h2 className="tw-text-lg tw-font-semibold">
                            {device.name || device.id}
                        </h2>
                        <div className="tw-text-sm tw-text-gray-500">
                            <strong>
                                {device.device?.model}
                                <br /> {device.device?.firmware_version}
                            </strong>
                        </div>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="tw-h-[calc(93vh-56px)] tw-flex tw-flex-col tw-relative">
                        {onShowLiveView && (
                            <div className="tw-p-4 tw-pb-0">
                                <button
                                    type="button"
                                    onClick={() => onShowLiveView(device)}
                                    className="tw-w-full tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded"
                                >
                                    Show LiveView
                                </button>
                            </div>
                        )}
                        <div className="tw-px-4 tw-pt-2 tw-pb-0 tw-flex">
                            <button
                                type="button"
                                onClick={() => setShowProfileModal(true)}
                                className={`tw-flex-1 tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-gray-900 tw-bg-yellow-400 hover:tw-bg-yellow-500 ${canWrite && onSaveAsProfile
                                        ? "tw-rounded-l"
                                        : "tw-rounded"
                                    }`}
                            >
                                Apply Device Profile
                            </button>
                            {canWrite && onSaveAsProfile && (
                                <Dropdown align="end">
                                    <Dropdown.Toggle
                                        as="button"
                                        className="tw-h-full tw-px-3 tw-text-gray-900 tw-bg-yellow-400 hover:tw-bg-yellow-500 tw-rounded-r tw-border-l tw-border-yellow-600"
                                    />
                                    <Dropdown.Menu>
                                        {/* Rarer, more deliberate action than "Apply" -
                                        tucked behind the caret so it's not equally
                                        weighted next to the primary button and
                                        harder to hit by accident. */}
                                        <Dropdown.Item
                                            onClick={() => setShowSaveAsProfileModal(true)}
                                        >
                                            Save Device Profile
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                        <div className="tw-flex tw-border-b tw-overflow-x-auto">
                            {SIDEBAR_TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`tw-px-4 tw-py-3 tw-text-sm tw-whitespace-nowrap tw-shrink-0 ${activeTab === tab.key
                                        ? "tw-border-b-2 tw-border-blue-500 tw-text-blue-500"
                                        : "tw-text-gray-500"
                                        }`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {profileActionBusy && (
                            <div className="tw-absolute tw-inset-0 tw-top-12 tw-bg-white/70 tw-flex tw-items-center tw-justify-center tw-z-10">
                                <div
                                    className="tw-w-8 tw-h-8 tw-border-4 tw-border-blue-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"
                                    role="status"
                                    aria-label="Working"
                                />
                            </div>
                        )}

                        <div className="tw-flex-1 tw-overflow-y-auto">
                            {activeTab === "signals" && (
                                <div className="tw-p-4">
                                    <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                                        These are your device's built-in signals. To give
                                        one a friendly name or set up how its data is read,
                                        use the Interpretation tab.
                                    </p>
                                    {visibleSignals.length === 0 && (
                                        <p className="tw-text-sm tw-text-gray-400">
                                            No signals configured yet.
                                        </p>
                                    )}
                                    {visibleSignals.map(({ signal }) => (
                                        <div
                                            key={signal.key}
                                            className="tw-border tw-rounded tw-p-3 tw-mb-3"
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                                <div className="tw-flex-1 tw-min-w-0">
                                                    <div className="tw-text-sm tw-truncate tw-font-mono">
                                                        {signal.key}
                                                    </div>
                                                </div>
                                                {canWrite && onSaveLogicalSignal && (
                                                    <button
                                                        type="button"
                                                        title={`Reset ${signal.key} to default`}
                                                        onClick={() =>
                                                            handleResetSignalToDefault(signal)
                                                        }
                                                        disabled={typeSavingKey === signal.key}
                                                        className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded disabled:tw-opacity-50 tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600 tw-shrink-0"
                                                    >
                                                        <i className="fa fa-undo" />
                                                    </button>
                                                )}
                                                {signal.isGpio && (
                                                    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
                                                        {(() => {
                                                            const decoded = decodeGpioType(
                                                                signal.type,
                                                                deviceFlags,
                                                                GPIO_TYPE_AXES,
                                                            );
                                                            return GPIO_TYPE_AXES.map((axis) => {
                                                                const current =
                                                                    decoded[axis.name] ?? axis.options[0];
                                                                const other =
                                                                    axis.options.find(
                                                                        (o) => o !== current,
                                                                    ) ?? axis.options[0];
                                                                const info = AXIS_ICON_INFO[current];
                                                                return (
                                                                    <button
                                                                        key={axis.name}
                                                                        type="button"
                                                                        title={info?.help}
                                                                        disabled={
                                                                            !canWrite ||
                                                                            typeSavingKey === signal.key
                                                                        }
                                                                        onClick={() =>
                                                                            handleAxisChange(
                                                                                signal,
                                                                                axis.name,
                                                                                other,
                                                                            )
                                                                        }
                                                                        className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded disabled:tw-opacity-50 tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100"
                                                                    >
                                                                        <i className={`fa ${info?.icon}`} />
                                                                    </button>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                )}
                                                {signal.isUart && (
                                                    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
                                                        {(() => {
                                                            const decoded = decodeUartType(
                                                                signal.type,
                                                                signal.manifestParams,
                                                                deviceFlags,
                                                            );
                                                            return UART_PARAMS.map((param) => {
                                                                const manifestParam =
                                                                    signal.manifestParams?.find(
                                                                        (p) => p.name === param.name,
                                                                    );
                                                                const options = manifestParam?.range || [];
                                                                if (options.length === 0) return null;
                                                                const current =
                                                                    decoded[param.name] ??
                                                                    manifestParam?.default ??
                                                                    "";
                                                                return (
                                                                    <select
                                                                        key={param.name}
                                                                        title={param.label}
                                                                        disabled={
                                                                            !canWrite ||
                                                                            typeSavingKey === signal.key
                                                                        }
                                                                        value={current}
                                                                        onChange={(e) =>
                                                                            handleUartParamChange(
                                                                                signal,
                                                                                param.name,
                                                                                e.target.value,
                                                                            )
                                                                        }
                                                                        className="tw-text-xs tw-border tw-rounded tw-px-1 tw-py-0.5 disabled:tw-opacity-50"
                                                                    >
                                                                        {options.map((opt) => (
                                                                            <option key={opt} value={opt}>
                                                                                {param.label}: {opt}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                            {((interpretationsBySource[signal.key] || []).length > 0 ||
                                                (canWrite && onSaveInterpretations)) && (
                                                    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-x-2 tw-gap-y-1 tw-mt-2 tw-pt-2 tw-border-t tw-border-dashed tw-text-xs">
                                                        {(interpretationsBySource[signal.key] || []).map(
                                                            (interp) => (
                                                                <button
                                                                    key={interp.name}
                                                                    type="button"
                                                                    title={`Go to interpretation "${interp.name}"`}
                                                                    onClick={() =>
                                                                        handleJumpToInterpretation(
                                                                            interp.name,
                                                                        )
                                                                    }
                                                                    className="tw-text-blue-600 hover:tw-underline"
                                                                >
                                                                    {interp.name}
                                                                </button>
                                                            ),
                                                        )}
                                                        {canWrite && onSaveInterpretations && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleCreateInterpretationFor(
                                                                        signal.key,
                                                                    )
                                                                }
                                                                className="tw-text-gray-500 hover:tw-text-blue-600 hover:tw-underline"
                                                            >
                                                                + create interpretation
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {activeTab === "interpretation" && (
                                <div className="tw-p-4">
                                    <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                                        Give a signal a friendly name and choose how to
                                        read its data.
                                    </p>
                                    {interpretations.length === 0 && (
                                        <p className="tw-text-sm tw-text-gray-400">
                                            No interpretations configured yet.
                                        </p>
                                    )}
                                    {interpretations.map((interp) => {
                                        const isEditing = editingKey === interp.name;
                                        const isHighlighted = highlightInterpName === interp.name;
                                        return (
                                            <div
                                                key={interp.name}
                                                ref={(el) => {
                                                    interpretationCardRefs.current[interp.name] = el;
                                                }}
                                                className={`tw-border tw-rounded tw-p-3 tw-mb-3 tw-transition-colors ${isHighlighted
                                                        ? "tw-border-blue-400 tw-bg-blue-50"
                                                        : ""
                                                    }`}
                                            >
                                                {!isEditing ? (
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <span
                                                            className="tw-w-3 tw-h-3 tw-rounded-full tw-shrink-0"
                                                            style={{
                                                                backgroundColor: `#${interp.color ||
                                                                    physicalSignalColors[
                                                                    interp.source
                                                                    ] ||
                                                                    "0080ff"
                                                                    }`,
                                                            }}
                                                        />
                                                        <div className="tw-flex-1 tw-min-w-0">
                                                            <div className="tw-text-sm tw-truncate">
                                                                {interp.name}
                                                            </div>
                                                            <div className="tw-text-xs tw-text-gray-400 tw-truncate">
                                                                via {interp.source} ·{" "}
                                                                {interp.analyzer?.kind}
                                                                {interp.inverted && " · Inverted"}
                                                                {interp.auxillary && " · Auxiliary"}
                                                            </div>
                                                        </div>
                                                        {canWrite && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleStartEditInterpretation(
                                                                        interp,
                                                                    )
                                                                }
                                                                className="tw-px-2 tw-py-1 tw-text-xs tw-rounded tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 tw-shrink-0"
                                                            >
                                                                Edit
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                                            Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={editName}
                                                            disabled={!canWrite}
                                                            onChange={(e) =>
                                                                setEditName(e.target.value)
                                                            }
                                                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded tw-text-sm tw-mb-2"
                                                            placeholder="e.g. soil sensor"
                                                        />

                                                        <div className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-2">
                                                            Source &amp; analyzer
                                                        </div>
                                                        <Select
                                                            options={physicalEndpointOptions.map(
                                                                (ep) => ({
                                                                    value: ep,
                                                                    label: ep,
                                                                    color: physicalSignalColors[ep],
                                                                    disabled:
                                                                        ep === "/uart" &&
                                                                        uartSourceTaken &&
                                                                        interp.source !== "/uart",
                                                                }),
                                                            )}
                                                            value={{
                                                                value: editEndpoint,
                                                                label: editEndpoint,
                                                                color: physicalSignalColors[
                                                                    editEndpoint
                                                                ],
                                                            }}
                                                            onChange={(opt) =>
                                                                setEditEndpoint(opt ? opt.value : "")
                                                            }
                                                            isOptionDisabled={(opt) => opt.disabled}
                                                            formatOptionLabel={formatSourceOption}
                                                            menuPortalTarget={document.body}
                                                            styles={selectMenuPortalStyles}
                                                            className="tw-mb-2 tw-text-sm"
                                                        />

                                                        {editEndpoint.startsWith("/i2c") && (
                                                            <div className="tw-flex tw-gap-2 tw-mb-2">
                                                                <input
                                                                    type="number"
                                                                    value={editI2cAddress}
                                                                    onChange={(e) =>
                                                                        setEditI2cAddress(
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                    placeholder="I2C address"
                                                                    className="tw-flex-1 tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm"
                                                                />
                                                                <input
                                                                    type="number"
                                                                    value={editI2cRegister}
                                                                    onChange={(e) =>
                                                                        setEditI2cRegister(
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                    placeholder="I2C register"
                                                                    className="tw-flex-1 tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm"
                                                                />
                                                            </div>
                                                        )}

                                                        <select
                                                            value={editAnalyzer.kind}
                                                            onChange={(e) =>
                                                                setEditAnalyzer(
                                                                    defaultAnalyzerConfig(
                                                                        e.target.value,
                                                                    ),
                                                                )
                                                            }
                                                            className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-mb-2"
                                                        >
                                                            {ANALYZER_KINDS.map((k) => (
                                                                <option key={k.value} value={k.value}>
                                                                    {k.label}
                                                                </option>
                                                            ))}
                                                        </select>

                                                        <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-2">
                                                            {(
                                                                ANALYZER_KIND_FIELDS[
                                                                editAnalyzer.kind
                                                                ] || []
                                                            ).map((f) => (
                                                                <div key={f.name}>
                                                                    <label className="tw-text-xs tw-text-gray-500 tw-block">
                                                                        {f.label}
                                                                    </label>
                                                                    {f.type === "select" ? (
                                                                        <select
                                                                            value={
                                                                                editAnalyzer[
                                                                                f.name
                                                                                ] ?? f.default
                                                                            }
                                                                            onChange={(e) =>
                                                                                setEditAnalyzer({
                                                                                    ...editAnalyzer,
                                                                                    [f.name]:
                                                                                        e.target
                                                                                            .value,
                                                                                })
                                                                            }
                                                                            className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm"
                                                                        >
                                                                            {f.options.map((opt) => (
                                                                                <option
                                                                                    key={opt.value}
                                                                                    value={opt.value}
                                                                                >
                                                                                    {opt.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <input
                                                                            type={
                                                                                f.type === "number"
                                                                                    ? "number"
                                                                                    : "text"
                                                                            }
                                                                            value={
                                                                                editAnalyzer[
                                                                                f.name
                                                                                ] ?? ""
                                                                            }
                                                                            onChange={(e) =>
                                                                                setEditAnalyzer({
                                                                                    ...editAnalyzer,
                                                                                    [f.name]:
                                                                                        f.type ===
                                                                                            "number"
                                                                                            ? Number(
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                            : e
                                                                                                .target
                                                                                                .value,
                                                                                })
                                                                            }
                                                                            className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm"
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                                            Color
                                                        </label>
                                                        <input
                                                            type="color"
                                                            value={`#${editInterpColor}`}
                                                            onChange={(e) =>
                                                                setEditInterpColor(
                                                                    e.target.value.replace("#", ""),
                                                                )
                                                            }
                                                            className="tw-w-full tw-h-8 tw-border tw-rounded tw-mb-3"
                                                        />

                                                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                                                            <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={editInterpInverted}
                                                                    onChange={(e) =>
                                                                        setEditInterpInverted(
                                                                            e.target.checked,
                                                                        )
                                                                    }
                                                                />
                                                                Inverted
                                                            </label>
                                                            <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={editInterpAuxillary}
                                                                    onChange={(e) =>
                                                                        setEditInterpAuxillary(
                                                                            e.target.checked,
                                                                        )
                                                                    }
                                                                />
                                                                Auxiliary
                                                            </label>
                                                        </div>

                                                        <div className="tw-flex tw-gap-2 tw-mt-3">
                                                            {canWrite && (
                                                                <button
                                                                    onClick={() =>
                                                                        handleSaveEditInterpretation(
                                                                            interp.name,
                                                                        )
                                                                    }
                                                                    disabled={savingEdit}
                                                                    className="tw-px-3 tw-py-1 tw-text-xs tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                                                >
                                                                    {savingEdit
                                                                        ? "Saving..."
                                                                        : "Save"}
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={handleCancelEdit}
                                                                className="tw-px-3 tw-py-1 tw-text-xs tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded"
                                                            >
                                                                Cancel
                                                            </button>
                                                            {canWrite && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleDeleteInterpretation(
                                                                            interp,
                                                                        )
                                                                    }
                                                                    disabled={savingEdit}
                                                                    className="tw-px-3 tw-py-1 tw-text-xs tw-text-red-600 hover:tw-bg-red-50 tw-rounded disabled:tw-opacity-50 tw-ms-auto"
                                                                >
                                                                    {(
                                                                        device?.device?.runtime
                                                                            ?.profileInterpretations || []
                                                                    ).some(
                                                                        (p) => p.name === interp.name,
                                                                    )
                                                                        ? "Reset to profile"
                                                                        : "Delete"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {canWrite && onSaveInterpretations && (
                                        <div
                                            ref={newInterpretationSectionRef}
                                            className="tw-mt-5 tw-pt-4 tw-border-t tw-border-dashed"
                                        >
                                            <div className="tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded tw-p-3">
                                                <div className="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-3">
                                                    + New Interpretation
                                                </div>

                                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                                    Name
                                                </label>
                                                <input
                                                    ref={newInterpretationNameInputRef}
                                                    type="text"
                                                    value={newSignalName}
                                                    onChange={(e) =>
                                                        setNewSignalName(e.target.value)
                                                    }
                                                    placeholder="e.g. soil sensor"
                                                    className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-rounded tw-bg-white tw-mb-2"
                                                />
                                                {newSignalName.trim() &&
                                                    (physicalEndpointOptions.includes(
                                                        newSignalName.trim(),
                                                    ) ||
                                                        interpretations.some(
                                                            (i) =>
                                                                i.name === newSignalName.trim(),
                                                        )) && (
                                                        <p className="tw-text-xs tw-text-red-600 tw-mb-2">
                                                            That name is already in use - pick a
                                                            different one.
                                                        </p>
                                                    )}

                                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                                    Source (which physical signal to read from)
                                                </label>
                                                <Select
                                                    options={physicalEndpointOptions.map((ep) => ({
                                                        value: ep,
                                                        label: ep,
                                                        color: physicalSignalColors[ep],
                                                        disabled: ep === "/uart" && uartSourceTaken,
                                                    }))}
                                                    value={(() => {
                                                        const ep =
                                                            newSignalEndpoint ||
                                                            physicalEndpointOptions[0] ||
                                                            "";
                                                        return {
                                                            value: ep,
                                                            label: ep,
                                                            color: physicalSignalColors[ep],
                                                        };
                                                    })()}
                                                    onChange={(opt) =>
                                                        setNewSignalEndpoint(opt ? opt.value : "")
                                                    }
                                                    isOptionDisabled={(opt) => opt.disabled}
                                                    formatOptionLabel={formatSourceOption}
                                                    menuPortalTarget={document.body}
                                                    styles={selectMenuPortalStyles}
                                                    className="tw-mb-2 tw-text-sm"
                                                />

                                                {(
                                                    newSignalEndpoint ||
                                                    physicalEndpointOptions[0] ||
                                                    ""
                                                ).startsWith("/i2c") && (
                                                        <div className="tw-flex tw-gap-2 tw-mb-2">
                                                            <input
                                                                type="number"
                                                                value={newSignalI2cAddress}
                                                                onChange={(e) =>
                                                                    setNewSignalI2cAddress(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="I2C address"
                                                                className="tw-flex-1 tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-bg-white"
                                                            />
                                                            <input
                                                                type="number"
                                                                value={newSignalI2cRegister}
                                                                onChange={(e) =>
                                                                    setNewSignalI2cRegister(
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="I2C register"
                                                                className="tw-flex-1 tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-bg-white"
                                                            />
                                                        </div>
                                                    )}

                                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                                    Packet format (how to decode what comes back)
                                                </label>
                                                <select
                                                    value={newSignalAnalyzer.kind}
                                                    onChange={(e) =>
                                                        setNewSignalAnalyzer(
                                                            defaultAnalyzerConfig(e.target.value),
                                                        )
                                                    }
                                                    className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-mb-2 tw-bg-white"
                                                >
                                                    {ANALYZER_KINDS.map((k) => (
                                                        <option key={k.value} value={k.value}>
                                                            {k.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-2">
                                                    {(
                                                        ANALYZER_KIND_FIELDS[
                                                        newSignalAnalyzer.kind
                                                        ] || []
                                                    ).map((f) => (
                                                        <div key={f.name}>
                                                            <label className="tw-text-xs tw-text-gray-500 tw-block">
                                                                {f.label}
                                                            </label>
                                                            {f.type === "select" ? (
                                                                <select
                                                                    value={
                                                                        newSignalAnalyzer[
                                                                        f.name
                                                                        ] ?? f.default
                                                                    }
                                                                    onChange={(e) =>
                                                                        setNewSignalAnalyzer({
                                                                            ...newSignalAnalyzer,
                                                                            [f.name]:
                                                                                e.target.value,
                                                                        })
                                                                    }
                                                                    className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-bg-white"
                                                                >
                                                                    {f.options.map((opt) => (
                                                                        <option
                                                                            key={opt.value}
                                                                            value={opt.value}
                                                                        >
                                                                            {opt.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <input
                                                                    type={
                                                                        f.type === "number"
                                                                            ? "number"
                                                                            : "text"
                                                                    }
                                                                    value={
                                                                        newSignalAnalyzer[f.name] ??
                                                                        ""
                                                                    }
                                                                    onChange={(e) =>
                                                                        setNewSignalAnalyzer({
                                                                            ...newSignalAnalyzer,
                                                                            [f.name]:
                                                                                f.type === "number"
                                                                                    ? Number(
                                                                                        e.target
                                                                                            .value,
                                                                                    )
                                                                                    : e.target
                                                                                        .value,
                                                                        })
                                                                    }
                                                                    className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-bg-white"
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                                    Color
                                                </label>
                                                <input
                                                    type="color"
                                                    value={`#${newInterpColor}`}
                                                    onChange={(e) =>
                                                        setNewInterpColor(
                                                            e.target.value.replace("#", ""),
                                                        )
                                                    }
                                                    className="tw-w-full tw-h-8 tw-border tw-rounded tw-mb-2"
                                                />

                                                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                                                    <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                                        <input
                                                            type="checkbox"
                                                            checked={newInterpInverted}
                                                            onChange={(e) =>
                                                                setNewInterpInverted(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                        Inverted
                                                    </label>
                                                    <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                                        <input
                                                            type="checkbox"
                                                            checked={newInterpAuxillary}
                                                            onChange={(e) =>
                                                                setNewInterpAuxillary(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                        Auxiliary
                                                    </label>
                                                </div>

                                                <button
                                                    onClick={handleAddInterpretation}
                                                    disabled={
                                                        !newSignalName.trim() ||
                                                        physicalEndpointOptions.includes(
                                                            newSignalName.trim(),
                                                        ) ||
                                                        interpretations.some(
                                                            (i) =>
                                                                i.name === newSignalName.trim(),
                                                        ) ||
                                                        ((newSignalEndpoint ||
                                                            physicalEndpointOptions[0]) ===
                                                            "/uart" &&
                                                            uartSourceTaken) ||
                                                        savingLogicalSignal
                                                    }
                                                    className="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                                >
                                                    {savingLogicalSignal
                                                        ? "Saving..."
                                                        : "Add Interpretation"}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeTab === "attributes" && (
                                <DeviceAttributesPanel device={device} />
                            )}
                            {activeTab === "locations" && (
                                <div className="tw-p-4">
                                    <div className="tw-mb-6">
                                        <label className="tw-block tw-mb-2">
                                            <span className="tw-text-sm tw-font-medium">
                                                Locations
                                            </span>
                                        </label>
                                        <div className="tw-flex tw-gap-2">
                                            <select
                                                value={selectedLocation}
                                                onChange={(e) =>
                                                    setSelectedLocation(e.target.value)
                                                }
                                                className="tw-flex-1 tw-form-select tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2"
                                            >
                                                <option value="">Select location</option>
                                                {AVAILABLE_LOCATIONS.map((location) => (
                                                    <option key={location} value={location}>
                                                        {location}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={handleAddLocation}
                                                disabled={!selectedLocation}
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="tw-text-sm tw-font-medium tw-mb-3">
                                            Linked Locations
                                        </h3>
                                        <div className="tw-space-y-2">
                                            {linkedLocations.map((location, index) => (
                                                <div
                                                    key={index}
                                                    className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-border tw-rounded-md"
                                                >
                                                    <span className="tw-text-sm">
                                                        {location}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            handleRemoveLocation(location)
                                                        }
                                                        className="tw-text-gray-400 hover:tw-text-red-500"
                                                    >
                                                        <MdOutlineBluetoothDisabled className="tw-w-5 tw-h-5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === "operations" &&
                                (saveOutcome ? (
                                    <div className="tw-p-4">
                                        <SaveSuccessPanel
                                            heading={saveOutcome.heading}
                                            subheading={saveOutcome.subheading}
                                            steps={saveOutcome.steps}
                                            onNavigate={handleStepNavigate}
                                        />
                                        <div className="tw-mt-4 tw-flex tw-justify-end">
                                            <button
                                                onClick={onClose}
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded"
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="tw-p-4">
                                        <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                                            Operations this device can perform. This
                                            is what makes it eligible when a job
                                            schedule needs that operation.
                                        </p>

                                        <label className="tw-block tw-mb-2">
                                            <span className="tw-text-sm tw-font-medium">
                                                Operations
                                            </span>
                                        </label>
                                        <Select
                                            options={operationOptions}
                                            placeholder={
                                                loadingOperations
                                                    ? "Loading operations..."
                                                    : "Select operations"
                                            }
                                            isMulti
                                            isDisabled={
                                                !canWrite ||
                                                loadingOperations ||
                                                loadingCapabilities
                                            }
                                            value={selectedOperations}
                                            onChange={(val) =>
                                                setSelectedOperations(val || [])
                                            }
                                            components={{ MultiValue: () => null }}
                                            closeMenuOnSelect={true}
                                            hideSelectedOptions={true}
                                            menuPortalTarget={document.body}
                                            styles={selectMenuPortalStyles}
                                        />

                                        {operationOptions.length === 0 &&
                                            !loadingOperations && (
                                                <div className="tw-text-sm tw-text-gray-500 tw-mt-3">
                                                    No operations defined for this
                                                    plant yet. Add them under Setup
                                                    &rarr; Operations.
                                                </div>
                                            )}

                                        <div className="tw-mt-6">
                                            <h3 className="tw-text-sm tw-font-medium tw-mb-3">
                                                Linked Operations
                                            </h3>
                                            {selectedOperations.length === 0 ? (
                                                <div className="tw-text-sm tw-text-gray-500">
                                                    No operations linked. This
                                                    device will not be recommended
                                                    for any operation.
                                                </div>
                                            ) : (
                                                <div className="tw-space-y-2">
                                                    {selectedOperations.map(
                                                        (operation) => (
                                                            <div
                                                                key={operation.value}
                                                                className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-border tw-rounded-md"
                                                            >
                                                                <span className="tw-text-sm">
                                                                    {operation.label}
                                                                </span>
                                                                {canWrite && (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleUnlinkOperation(
                                                                                operation.value,
                                                                            )
                                                                        }
                                                                        className="tw-text-gray-400 hover:tw-text-red-500"
                                                                        title={`Unlink ${operation.label}`}
                                                                    >
                                                                        <i
                                                                            className="fa fa-unlink"
                                                                            aria-hidden="true"
                                                                        />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-3">
                                            <button
                                                onClick={onClose}
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-border tw-rounded"
                                            >
                                                Discard
                                            </button>
                                            <button
                                                onClick={handleSaveOperations}
                                                disabled={
                                                    !canWrite || savingOperations
                                                }
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                            >
                                                {savingOperations
                                                    ? "Saving..."
                                                    : "Save & Update"}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <Modal
                show={showProfileModal}
                onHide={() => setShowProfileModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Apply Device Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="tw-mb-3">
                        <label className="tw-block tw-mb-2">
                            <span className="tw-text-sm tw-font-medium">
                                Device Profile
                            </span>
                        </label>
                        <Select
                            isDisabled={!canWrite}
                            options={profileOptions}
                            value={selectedProfileOption}
                            onChange={(opt) =>
                                setSelectedProfileId(opt ? opt.value : "")
                            }
                            isOptionDisabled={(opt) => opt.isDisabled}
                            placeholder="-- Select a profile --"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            styles={selectMenuPortalStyles}
                        />
                        {deviceModel && (
                            <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500 tw-mt-2">
                                <input
                                    type="checkbox"
                                    checked={showIncompatibleProfiles}
                                    onChange={(e) =>
                                        setShowIncompatibleProfiles(
                                            e.target.checked,
                                        )
                                    }
                                />
                                Show incompatible profiles
                            </label>
                        )}
                    </div>

                    <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-mb-4">
                        <input
                            type="checkbox"
                            disabled={!canWrite}
                            checked={rebootNow}
                            onChange={(e) =>
                                setRebootNow(e.target.checked)
                            }
                        />
                        Reboot device now to apply signal role
                        changes immediately
                    </label>

                    <p className="tw-text-xs tw-text-gray-400 tw-mb-4">
                        A profile is only a starting point - anything
                        you've set directly on this device (here, or
                        in the Signals/Interpretation tabs) always
                        wins over it, and can be reset back to the
                        profile's value any time. Takes effect the
                        next time this device reconnects, unless you
                        reboot it now.
                    </p>

                    {canWrite && (
                        <div className="tw-flex tw-gap-2">
                            <button
                                onClick={handleAssignProfile}
                                disabled={
                                    !selectedProfileId ||
                                    profileActionBusy ||
                                    selectedProfileId ===
                                    String(assignedProfile?.id ?? "")
                                }
                                className="tw-flex-1 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                            >
                                {profileActionBusy
                                    ? "Working..."
                                    : assignedProfile
                                        ? "Switch Profile"
                                        : "Assign Profile"}
                            </button>
                            {assignedProfile && (
                                <button
                                    onClick={handleUnassignProfile}
                                    disabled={profileActionBusy}
                                    className="tw-flex-1 tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 tw-border tw-border-red-300 hover:tw-bg-red-50 tw-rounded disabled:tw-opacity-50"
                                >
                                    Unassign
                                </button>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
            <Modal
                show={showSaveAsProfileModal}
                onHide={() => setShowSaveAsProfileModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Save Device Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="tw-text-xs tw-text-gray-400 tw-mb-4">
                        Snapshots this device's current signals, interpretations,
                        and attributes into a brand new, reusable profile. Doesn't
                        change anything about this device itself.
                    </p>
                    <label className="tw-block tw-mb-2">
                        <span className="tw-text-sm tw-font-medium">
                            Profile Name
                        </span>
                    </label>
                    <input
                        type="text"
                        value={saveAsProfileName}
                        onChange={(e) => setSaveAsProfileName(e.target.value)}
                        placeholder="e.g. Line 3 Sewing Machine"
                        className="tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-mb-2"
                    />
                    <label className="tw-block tw-mb-2">
                        <span className="tw-text-sm tw-font-medium">
                            Description
                        </span>
                    </label>
                    <textarea
                        value={saveAsProfileDescription}
                        onChange={(e) =>
                            setSaveAsProfileDescription(e.target.value)
                        }
                        placeholder="Optional description..."
                        rows={2}
                        className="tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-mb-3"
                    ></textarea>
                    <button
                        onClick={handleSaveAsProfile}
                        disabled={!saveAsProfileName || savingAsProfile}
                        className="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                    >
                        {savingAsProfile ? "Saving..." : "Save as Profile"}
                    </button>
                </Modal.Body>
            </Modal>
            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                onConfirm={confirmCfg.onConfirm}
                onClose={closeConfirm}
            />
        </>
    );
};

export default DeviceEditSidebar;
